import { useQuery } from "@apollo/client";
import SideSheetModal from "@/components/SideSheetModal";
import { MaterialNames } from "@/components/material_symbol_names";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import NoteSideSheetContent from "./NoteSideSheetContent";
import NoteSideSheetSkeleton from "./NoteSideSheetSkeleton";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import { GET_NOTE_QUERY } from "../NotesTable/queries";
import { CRMNote } from "@/types";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick?: () => void;
  testId?: string;
  iconName?: MaterialNames;
  ariaLabel?: string;
  /** When true, the action only appears in the overflow menu (never inline). */
  menuOnly?: boolean;
  /** Analytics-safe field name for whole-row menu copy actions. */
  copyFieldLabel?: string;
  /** Internal system identifier for Amplitude debug attributes on allowlisted ID fields. */
  copyFieldId?: string;
  copyButton?: {
    textToCopy: string;
    ariaLabel?: string;
    copyFieldLabel?: string;
    copyFieldId?: string;
    feedback?: "tooltip" | "snackbar" | "none";
    successMessage?: string;
    onCopied?: () => void;
  };
}

export type NoteSideSheetProps = {
  open: boolean;
  onClose: () => void;
  noteId: string | null;
  title?: string;
  customActionButtons?: CustomActionButton[];
  onParentClick?: () => void;
  showSource?: boolean;
  onEditNote?: () => void;
  onAddTask?: () => void;
  onViewAllNotes?: () => void;
  getCopyLink?: (note: CRMNote) => string | null | undefined;
};

export default function NoteSideSheet({
  open,
  onClose,
  noteId,
  title = "Note Details",
  customActionButtons,
  onParentClick,
  showSource = true,
  onEditNote,
  onAddTask,
  onViewAllNotes,
  getCopyLink
}: NoteSideSheetProps) {
  const { data, loading, error } = useQuery(GET_NOTE_QUERY, {
    variables: { id: noteId },
    fetchPolicy: "cache-first",
    skip: !noteId || !open
  });

  const note = data?.note;

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  const actionButtons: CustomActionButton[] = [
    ...(onEditNote
      ? [
          {
            text: "Edit Note",
            variant: "outlined" as const,
            onClick: onEditNote
          }
        ]
      : []),
    ...(onViewAllNotes
      ? [
          {
            text: "View All Notes",
            variant: "text" as const,
            onClick: onViewAllNotes
          }
        ]
      : []),
    ...(onAddTask
      ? [
          {
            text: "Add Task",
            variant: "text" as const,
            onClick: onAddTask,
            iconName: "add" as const,
            testId: "add-task"
          }
        ]
      : []),
    ...(getCopyLink && note
      ? (() => {
          const url = getCopyLink(note);
          if (!url) return [];
          return [
            {
              text: "Copy Link",
              variant: "text" as const,
              testId: "copy-note-link-button",
              menuOnly: true,
              iconName: "content_copy" as const,
              copyFieldLabel: "note link",
              copyFieldId: note.id,
              onClick: () => {
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(url);
                    showSnackbar("Link copied to clipboard");
                  } catch {
                    showErrorSnackbar("Failed to copy link to clipboard");
                  }
                })();
              }
            }
          ];
        })()
      : []),
    ...(customActionButtons || [])
  ];

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={title} actions={null}>
      {loading ? (
        <NoteSideSheetSkeleton />
      ) : error ? (
        <ErrorBoundary message={error.message} variant="inline" />
      ) : note ? (
        <NoteSideSheetContent
          note={note}
          customActionButtons={actionButtons}
          onParentClick={onParentClick}
          showSource={showSource}
        />
      ) : (
        <EmptyState iconName="sticky_note_2" title="Note Not Found" />
      )}
    </SideSheetModal>
  );
}
